// pages/setup/components/PageOverrideEditor.tsx
//
// Editor de uma regra por página: path, nome e, por bloco, Herdar/Ligar/Desligar.
import type { ClipboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AlertCircle, MousePointerClick, Trash2, X } from 'lucide-react';
import type { PageBlockOverride, PageOverrideBlockKey } from '@/types/ad-setup';
import { Tooltip } from './Tooltip';
import {
    MAX_INSIDE_CONTAINER,
    MAX_LABEL_LENGTH,
    MIN_INSIDE_CONTAINER,
    PAGE_OVERRIDE_BLOCKS,
    blockMode,
    normalizePagePath,
    type BlockMode,
    type PageOverrideBlockDef,
    type PageOverrideDraft,
} from '../pageOverrides';

/** Valor do bloco no site, só o que o editor precisa mostrar. */
export interface SiteBlockSnapshot {
    enabled: boolean;
    include_in?: string[];
    exclude_from?: string[];
    reference?: string;
    insideContainer?: number;
    positionReference?: 'before' | 'after';
    lazyLoad?: boolean;
    fixedPosition?: 'top' | 'bottom';
}

interface PageOverrideEditorProps {
    draft: PageOverrideDraft;
    siteBlocks: Record<PageOverrideBlockDef['siteKey'], SiteBlockSnapshot>;
    errors: string[];
    /** Bloco da regra esperando clique no preview (modo de seleção). */
    pickingBlock: PageOverrideBlockKey | null;
    onChange: (draft: PageOverrideDraft) => void;
    onPickFromPreview: (block: PageOverrideBlockKey | null) => void;
    onRemove: () => void;
}

const MODE_OPTIONS: { value: BlockMode; label: string }[] = [
    { value: 'inherit', label: 'Herdar do site' },
    { value: 'on', label: 'Ligar' },
    { value: 'off', label: 'Desligar' },
];

const selectClass =
    'flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs';

function describeSite(def: PageOverrideBlockDef, site: SiteBlockSnapshot | undefined): string {
    if (!site) return 'no site: —';
    if (!site.enabled) return 'no site: desligado';
    if (def.kind === 'positioned') {
        const pos = site.positionReference === 'before' ? 'antes do' : 'depois do';
        return `no site: ligado, ${pos} ${site.insideContainer}º <${site.reference}>`;
    }
    if (def.kind === 'sticky') {
        return `no site: ligado, ${site.fixedPosition === 'top' ? 'no topo' : 'no rodapé'}`;
    }
    return 'no site: ligado';
}

export function PageOverrideEditor({
    draft,
    siteBlocks,
    errors,
    pickingBlock,
    onChange,
    onPickFromPreview,
    onRemove,
}: PageOverrideEditorProps) {
    const setBlock = (key: PageOverrideBlockKey, block: PageBlockOverride | undefined) => {
        const blocks = { ...draft.blocks };
        if (block) blocks[key] = block;
        else delete blocks[key];
        onChange({ ...draft, blocks });
    };

    const setMode = (key: PageOverrideBlockKey, mode: BlockMode) => {
        const current = draft.blocks[key];
        if (mode === 'inherit') setBlock(key, undefined);
        // Desligar descarta posição: com o bloco desligado ela não faz nada.
        else if (mode === 'off') setBlock(key, { enabled: false });
        else setBlock(key, { ...(current?.enabled === false ? {} : current), enabled: true });
        if (mode !== 'on' && pickingBlock === key) onPickFromPreview(null);
    };

    const setField = <F extends keyof PageBlockOverride>(
        key: PageOverrideBlockKey,
        field: F,
        value: PageBlockOverride[F] | undefined
    ) => {
        const next: PageBlockOverride = { ...draft.blocks[key] };
        if (value === undefined) delete next[field];
        else next[field] = value;
        setBlock(key, next);
    };

    const handlePathPaste = (e: ClipboardEvent<HTMLInputElement>) => {
        const text = e.clipboardData.getData('text');
        if (/^\s*https?:\/\//i.test(text)) {
            e.preventDefault();
            onChange({ ...draft, path: normalizePagePath(text) });
        }
    };

    const isPrefix = draft.path.trim().endsWith('*');

    const renderPositionedFields = (def: PageOverrideBlockDef, block: PageBlockOverride) => {
        const site = siteBlocks[def.siteKey];
        const isPicking = pickingBlock === def.key;
        return (
            <div className="space-y-2 pt-2">
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <Label className="flex items-center text-[11px]">
                            Referência HTML
                            <Tooltip text="Vazio herda do site. Ex.: p, h2, div" />
                        </Label>
                        <Input
                            value={block.reference ?? ''}
                            onChange={(e) =>
                                setField(def.key, 'reference', e.target.value === '' ? undefined : e.target.value)
                            }
                            placeholder={site?.reference ? `${site.reference} (site)` : 'herda'}
                            className="h-8 text-xs"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="flex items-center text-[11px]">
                            Posição Container
                            <Tooltip text={`Vazio herda do site. De ${MIN_INSIDE_CONTAINER} a ${MAX_INSIDE_CONTAINER}.`} />
                        </Label>
                        <Input
                            type="number"
                            min={MIN_INSIDE_CONTAINER}
                            max={MAX_INSIDE_CONTAINER}
                            value={block.inside_container ?? ''}
                            onChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                setField(def.key, 'inside_container', Number.isNaN(n) ? undefined : n);
                            }}
                            placeholder={site?.insideContainer ? `${site.insideContainer} (site)` : 'herda'}
                            className="h-8 text-xs"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <Label className="text-[11px]">Posicionamento</Label>
                        <select
                            value={block.position_reference ?? ''}
                            onChange={(e) =>
                                setField(
                                    def.key,
                                    'position_reference',
                                    e.target.value === '' ? undefined : (e.target.value as 'before' | 'after')
                                )
                            }
                            className={selectClass}
                        >
                            <option value="">
                                Herdar ({site?.positionReference === 'before' ? 'antes' : 'depois'})
                            </option>
                            <option value="before">Antes</option>
                            <option value="after">Depois</option>
                        </select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[11px]">Lazy load</Label>
                        <select
                            value={block.lazy_load === undefined ? '' : block.lazy_load ? 'true' : 'false'}
                            onChange={(e) =>
                                setField(
                                    def.key,
                                    'lazy_load',
                                    e.target.value === '' ? undefined : e.target.value === 'true'
                                )
                            }
                            className={selectClass}
                        >
                            <option value="">Herdar ({site?.lazyLoad ? 'sim' : 'não'})</option>
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                        </select>
                    </div>
                </div>
                <Button
                    type="button"
                    variant={isPicking ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onPickFromPreview(isPicking ? null : def.key)}
                    className={`w-full h-7 text-[11px] ${isPicking ? 'bg-green-600 hover:bg-green-700' : ''}`}
                >
                    {isPicking ? (
                        <>
                            <X className="h-3 w-3 mr-1.5" />
                            Clique no elemento do preview (cancelar)
                        </>
                    ) : (
                        <>
                            <MousePointerClick className="h-3 w-3 mr-1.5" />
                            Selecionar posição no preview
                        </>
                    )}
                </Button>
            </div>
        );
    };

    const renderStickyFields = (def: PageOverrideBlockDef, block: PageBlockOverride) => {
        const site = siteBlocks[def.siteKey];
        return (
            <div className="space-y-1 pt-2">
                <Label className="text-[11px]">Posição Fixa</Label>
                <select
                    value={block.fixed_position ?? ''}
                    onChange={(e) =>
                        setField(
                            def.key,
                            'fixed_position',
                            e.target.value === '' ? undefined : (e.target.value as 'top' | 'bottom')
                        )
                    }
                    className={selectClass}
                >
                    <option value="">Herdar ({site?.fixedPosition === 'top' ? 'topo' : 'rodapé'})</option>
                    <option value="top">Topo</option>
                    <option value="bottom">Rodapé</option>
                </select>
            </div>
        );
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Regra por página</h3>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onRemove}
                    className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    <span className="text-xs">Remover</span>
                </Button>
            </div>

            {errors.length > 0 && (
                <div className="rounded-md border border-red-200 bg-red-50 p-2 space-y-0.5">
                    {errors.map((message) => (
                        <p key={message} className="flex items-start gap-1.5 text-[11px] text-red-700">
                            <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            {message}
                        </p>
                    ))}
                </div>
            )}

            <div className="space-y-1.5">
                <Label className="flex items-center text-xs">
                    Path da página
                    <Tooltip text="Cole a URL inteira ou só o path. /meu-artigo/ vale só para essa página; /rec-* vale para todas que começam com /rec-" />
                </Label>
                <Input
                    value={draft.path}
                    onChange={(e) => onChange({ ...draft, path: e.target.value })}
                    onBlur={() => {
                        const normalized = normalizePagePath(draft.path);
                        if (normalized !== draft.path) onChange({ ...draft, path: normalized });
                    }}
                    onPaste={handlePathPaste}
                    placeholder="/meu-artigo/ ou /rec-*"
                    className="h-8 text-xs font-mono"
                />
                <p className="text-[10px] text-gray-500">
                    {isPrefix
                        ? 'Prefixo: vale para todas as páginas que começam assim.'
                        : 'Exato: vale só para esta página (a barra final é ignorada).'}
                </p>
            </div>

            <div className="space-y-1.5">
                <Label className="text-xs">Nome (opcional)</Label>
                <Input
                    value={draft.label}
                    maxLength={MAX_LABEL_LENGTH}
                    onChange={(e) => onChange({ ...draft, label: e.target.value })}
                    placeholder="Ex.: Artigos de receita"
                    className="h-8 text-xs"
                />
            </div>

            <div className="pt-2 border-t border-gray-200 space-y-2">
                <p className="text-[11px] text-gray-500">
                    Bloco em "Herdar" segue o site. Num bloco alterado aqui, os filtros
                    "Incluir apenas em"/"Excluir de" do site deixam de valer nesta página.
                </p>
                {PAGE_OVERRIDE_BLOCKS.map((def) => {
                    const block = draft.blocks[def.key];
                    const mode = blockMode(block);
                    const site = siteBlocks[def.siteKey];
                    const siteHasFilter =
                        (site?.include_in?.length ?? 0) > 0 || (site?.exclude_from?.length ?? 0) > 0;
                    return (
                        <div
                            key={def.key}
                            className={`rounded-lg border p-2 ${
                                mode === 'inherit'
                                    ? 'border-gray-200 bg-white'
                                    : mode === 'on'
                                      ? 'border-green-200 bg-green-50/40'
                                      : 'border-gray-300 bg-gray-50'
                            }`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-medium text-gray-900">{def.label}</span>
                                <span className="text-[10px] text-gray-500 truncate" title={describeSite(def, site)}>
                                    {describeSite(def, site)}
                                </span>
                            </div>
                            {siteHasFilter && mode !== 'inherit' && (
                                <p className="text-[10px] text-amber-700 mt-0.5">
                                    O site tem filtro de páginas neste bloco; aqui ele não vale.
                                </p>
                            )}
                            <div className="mt-1.5 grid grid-cols-3 gap-1" role="radiogroup" aria-label={def.label}>
                                {MODE_OPTIONS.map((option) => {
                                    const active = mode === option.value;
                                    const activeClass =
                                        option.value === 'on'
                                            ? 'bg-green-600 text-white border-green-600'
                                            : option.value === 'off'
                                              ? 'bg-gray-700 text-white border-gray-700'
                                              : 'bg-white text-gray-900 border-gray-400';
                                    return (
                                        <button
                                            key={option.value}
                                            type="button"
                                            role="radio"
                                            aria-checked={active}
                                            onClick={() => setMode(def.key, option.value)}
                                            className={`h-7 rounded-md border text-[11px] font-medium transition-colors ${
                                                active
                                                    ? activeClass
                                                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            {option.label}
                                        </button>
                                    );
                                })}
                            </div>
                            {mode === 'on' && block && def.kind === 'positioned' && renderPositionedFields(def, block)}
                            {mode === 'on' && block && def.kind === 'sticky' && renderStickyFields(def, block)}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
